import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import type { Player } from "../entities/Player";
import type { ArenaScene } from "./ArenaScene";

const HUD_PAD = 14;
const BAR_WIDTH = 220;
const BAR_HEIGHT = 14;
const HP_COLOR = 0xef4444;
const MANA_COLOR = 0x38bdf8;
const BAR_BG = 0x0f172a;
const BAR_BORDER = 0x334155;

interface PlayerHud {
  nameText: Phaser.GameObjects.Text;
  hpFill: Phaser.GameObjects.Rectangle;
  hpText: Phaser.GameObjects.Text;
  manaFill: Phaser.GameObjects.Rectangle;
  manaText: Phaser.GameObjects.Text;
  player: Player;
}

/**
 * Overlay HUD that runs in parallel with ArenaScene. Reads players from the
 * arena and updates bars/text on each player event.
 */
export class HUDScene extends Phaser.Scene {
  private p1!: PlayerHud;
  private p2!: PlayerHud;

  constructor() {
    super("HUDScene");
  }

  create(): void {
    const arena = this.scene.get("ArenaScene") as ArenaScene;
    if (!arena.player1 || !arena.player2) {
      // ArenaScene's create() hasn't completed yet. Defer.
      this.time.delayedCall(0, () => this.create());
      return;
    }

    this.p1 = this.buildPlayerHud(arena.player1, HUD_PAD, "left");
    this.p2 = this.buildPlayerHud(arena.player2, GAME_WIDTH - HUD_PAD - BAR_WIDTH, "right");

    this.wireEvents(arena.player1, this.p1);
    this.wireEvents(arena.player2, this.p2);
  }

  private buildPlayerHud(
    player: Player,
    x: number,
    align: "left" | "right",
  ): PlayerHud {
    const y = HUD_PAD;
    const nameText = this.add
      .text(align === "left" ? x : x + BAR_WIDTH, y, player.name, {
        fontFamily: "monospace",
        fontSize: "16px",
        color: player.which === "player1" ? "#f97316" : "#22d3ee",
        backgroundColor: "rgba(15, 23, 42, 0.8)",
        padding: { left: 6, right: 6, top: 2, bottom: 2 },
      })
      .setOrigin(align === "left" ? 0 : 1, 0)
      .setDepth(100);

    const hpY = y + 24;
    this.add
      .rectangle(x, hpY, BAR_WIDTH, BAR_HEIGHT, BAR_BG)
      .setOrigin(0, 0)
      .setStrokeStyle(1, BAR_BORDER)
      .setDepth(100);
    const hpFill = this.add
      .rectangle(x, hpY, BAR_WIDTH, BAR_HEIGHT, HP_COLOR)
      .setOrigin(0, 0)
      .setDepth(101);
    const hpText = this.add
      .text(x + BAR_WIDTH / 2, hpY + BAR_HEIGHT / 2, `${player.hp} / ${player.maxHp}`, {
        fontFamily: "monospace",
        fontSize: "11px",
        color: "#ffffff",
      })
      .setOrigin(0.5)
      .setDepth(102);

    const manaY = hpY + BAR_HEIGHT + 4;
    this.add
      .rectangle(x, manaY, BAR_WIDTH, BAR_HEIGHT, BAR_BG)
      .setOrigin(0, 0)
      .setStrokeStyle(1, BAR_BORDER)
      .setDepth(100);
    const manaFill = this.add
      .rectangle(x, manaY, BAR_WIDTH, BAR_HEIGHT, MANA_COLOR)
      .setOrigin(0, 0)
      .setDepth(101);
    const manaText = this.add
      .text(x + BAR_WIDTH / 2, manaY + BAR_HEIGHT / 2, `${player.mana} / ${player.maxMana}`, {
        fontFamily: "monospace",
        fontSize: "11px",
        color: "#ffffff",
      })
      .setOrigin(0.5)
      .setDepth(102);

    // Footer hint, only render once (under p1 only).
    if (player.which === "player1") {
      this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT - 18, "Esc to pause   ·   M to return to menu", {
          fontFamily: "monospace",
          fontSize: "12px",
          color: "#64748b",
        })
        .setOrigin(0.5)
        .setDepth(100);
    }

    return { nameText, hpFill, hpText, manaFill, manaText, player };
  }

  private wireEvents(player: Player, hud: PlayerHud): void {
    player.on("damaged", () => this.refresh(hud));
    player.on("healed", () => this.refresh(hud));
    player.on("manaChanged", () => this.refresh(hud));
    player.on("died", () => {
      hud.nameText.setColor("#64748b");
      this.refresh(hud);
    });
  }

  private refresh(hud: PlayerHud): void {
    const p = hud.player;
    hud.hpFill.width = Math.max(0, (p.hp / p.maxHp) * BAR_WIDTH);
    hud.hpText.setText(`${p.hp} / ${p.maxHp}`);
    hud.manaFill.width = Math.max(0, (p.mana / p.maxMana) * BAR_WIDTH);
    hud.manaText.setText(`${p.mana} / ${p.maxMana}`);
  }
}
